/**
 * Agency Admin Service
 * Handles API calls for agency admin functionality
 */

class AgencyAdminService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '';
    this.initialized = false;
  }

  initialize() {
    if (!this.initialized) {
      this.baseURL = import.meta.env.VITE_API_URL || '';
      this.initialized = true;
    }
  }

  /**
   * Make authenticated API request
   * @param {string} endpoint - API endpoint path
   * @param {Object} options - Fetch options (method, body, headers, etc.)
   * @returns {Promise<Object>} Parsed JSON response
   * @throws {Error} Error with code, message, status, and rawBody properties
   */
  async makeRequest(endpoint, options = {}) {
    this.initialize();
    
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('token');
    
    // Build default headers
    const defaultHeaders = {
      'Authorization': token ? `Bearer ${token}` : '',
    };

    // Set Content-Type only for JSON string bodies
    // Never set Content-Type for FormData (browser handles it with boundary)
    const hasBody = options.body !== undefined;
    const isFormData = options.body instanceof FormData;
    const isJsonString = hasBody && !isFormData && typeof options.body === 'string';
    const looksLikeJson = isJsonString && (options.body.trim().startsWith('{') || options.body.trim().startsWith('['));
    
    if (looksLikeJson) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Merge headers: defaultHeaders first, then options.headers (options.headers takes precedence)
    const finalHeaders = {
      ...defaultHeaders,
      ...(options.headers || {})
    };

    const defaultOptions = {
      method: 'GET',
      credentials: 'include',
    };

    // Merge options: defaultOptions first, then options, but override headers with finalHeaders
    const finalOptions = {
      ...defaultOptions,
      ...options,
      headers: finalHeaders
    };

    try {
      const response = await fetch(url, finalOptions);
      
      if (!response.ok) {
        // Try to parse error response as JSON
        let errorData = null;
        let rawBody = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
          } else {
            // If not JSON, read as text (truncate to 500 chars for debugging)
            const text = await response.text();
            rawBody = text.length > 500 ? text.substring(0, 500) + '...' : text;
          }
        } catch (parseError) {
          // If parsing fails, try to read as text for debugging
          try {
            const text = await response.text();
            rawBody = text.length > 500 ? text.substring(0, 500) + '...' : text;
          } catch (textError) {
            // If even text reading fails, continue with generic error
            console.warn('Failed to read error response body:', textError);
          }
        }

        const error = new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        
        // Attach error code and message from JSON response if available
        if (errorData) {
          if (errorData.code) {
            error.code = errorData.code;
          }
          if (errorData.message) {
            error.message = errorData.message;
          }
          // Also attach full response data for debugging
          error.response = errorData;
        }
        
        // Attach raw body for debugging if JSON parse failed
        if (rawBody) {
          error.rawBody = rawBody;
        }
        
        throw error;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      // If error already has code/message from above, preserve it
      if (!error.code && !error.message.includes('HTTP error')) {
        console.error('Agency Admin API request failed:', error);
      }
      throw error;
    }
  }

  // Dashboard & Analytics
  async getDashboardSummary() {
    return this.makeRequest('/api/agency-admin/dashboard/summary');
  }

  async getAgencyMetrics(agencyId, period = '30d') {
    return this.makeRequest(`/api/agency-admin/dashboard/metrics?period=${period}`);
  }

  async getAgentPerformance(agencyId, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.makeRequest(`/api/agency-admin/dashboard/agent-performance?${queryParams}`);
  }

  async getRevenueAnalytics(agencyId, period = '30d') {
    return this.makeRequest(`/api/agency-admin/dashboard/revenue-analytics?period=${period}`);
  }

  async getRecentActivity(agencyId, limit = 10) {
    return this.makeRequest(`/api/agency-admin/dashboard/recent-activity?limit=${limit}`);
  }

  async getBusinessIntelligence(agencyId, period = '30d') {
    return this.makeRequest(`/api/agency-admin/dashboard/business-intelligence?period=${period}`);
  }

  // Agent Management
  async getAgents(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.makeRequest(`/api/agency-admin/agents?${queryParams}`);
  }

  async getAgent(agentId) {
    return this.makeRequest(`/api/agency-admin/agents/${agentId}`);
  }

  /**
   * Create a new agent for the agency
   * @param {Object} agentData - Agent data (firstname, lastname, email, phone, role, status)
   * @returns {Promise<Object>} Created agent data
   * @throws {Error} Error with code (e.g., 'VALIDATION_ERROR', 'SEAT_LIMIT_REACHED', 'ADMIN_LIMIT_REACHED')
   */
  async createAgent(agentData) {
    return this.makeRequest(`/api/agency-admin/agents`, {
      method: 'POST',
      body: JSON.stringify(agentData)
    });
  }

  async updateAgent(agentId, updates) {
    return this.makeRequest(`/api/agency-admin/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteAgent(agentId) {
    return this.makeRequest(`/api/agency-admin/agents/${agentId}`, {
      method: 'DELETE'
    });
  }

  async setCommissionStructure(agencyId, structure) {
    return this.makeRequest(`/api/agency-admin/agents/commission-structure`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, structure })
    });
  }

  /**
   * Resend password setup email to an agent
   * Generates a new password reset token and sends setup email
   * @param {string} agentId - Agent user ID
   * @returns {Promise<Object>} Response with success status
   * @throws {Error} Error with code (e.g., 'AGENT_NOT_FOUND', 'SEND_FAILED')
   */
  async resendPasswordSetup(agentId) {
    return this.makeRequest(`/api/agency-admin/agents/${agentId}/password-setup`, {
      method: 'POST'
    });
  }

  // NOTE: inviteAgent is NOT IMPLEMENTED - invites do not exist in the system
  // This method is deprecated and will be removed in a future version
  // Use createAgent() instead, which automatically sends password setup emails
  /**
   * @deprecated Invites are not implemented. Use createAgent() instead.
   */
  async inviteAgent(agencyId, inviteData) {
    console.warn('inviteAgent() is deprecated. Invites do not exist. Use createAgent() instead.');
    throw new Error('Invites are not implemented. Use createAgent() instead.');
  }

  // Financial Management
  async getCommissionData(agencyId, period = '30d') {
    return this.makeRequest(`/api/agency-admin/financial/commissions?period=${period}`);
  }

  async processCommissionPayout(agencyId, payoutData) {
    return this.makeRequest(`/api/agency-admin/financial/commissions/payout`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, ...payoutData })
    });
  }

  async getFinancialReports(agencyId, reportType, period = '30d') {
    return this.makeRequest(`/api/agency-admin/financial/reports/${reportType}?period=${period}`);
  }

  async getRevenueBreakdown(agencyId, period = '30d') {
    return this.makeRequest(`/api/agency-admin/financial/revenue-breakdown?period=${period}`);
  }

  // Branding & White-labeling
  async getBranding(agencyId) {
    return this.makeRequest(`/api/agency-admin/branding?agencyId=${agencyId}`);
  }

  async updateBranding(agencyId, brandingData) {
    return this.makeRequest(`/api/agency-admin/branding`, {
      method: 'PUT',
      body: JSON.stringify({ agencyId, ...brandingData })
    });
  }

  async uploadBrandAssets(agencyId, assets) {
    const formData = new FormData();
    Object.keys(assets).forEach(key => {
      formData.append(key, assets[key]);
    });
    formData.append('agencyId', agencyId);

    return this.makeRequest(`/api/agency-admin/branding/assets`, {
      method: 'POST',
      body: formData
      // Note: Content-Type header is automatically omitted for FormData
    });
  }

  async configureDomain(agencyId, domainConfig) {
    return this.makeRequest(`/api/agency-admin/branding/domain`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, ...domainConfig })
    });
  }

  async getBrandingPreview(agencyId) {
    return this.makeRequest(`/api/agency-admin/branding/preview?agencyId=${agencyId}`);
  }

  // Reports & Analytics
  async generateCustomReport(agencyId, reportConfig) {
    return this.makeRequest(`/api/agency-admin/reports/custom`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, ...reportConfig })
    });
  }

  async getReportTemplates(agencyId) {
    return this.makeRequest(`/api/agency-admin/reports/templates?agencyId=${agencyId}`);
  }

  async scheduleReport(agencyId, scheduleConfig) {
    return this.makeRequest(`/api/agency-admin/reports/schedule`, {
      method: 'POST',
      body: JSON.stringify({ agencyId, ...scheduleConfig })
    });
  }

  // Agency Settings
  async getAgencySettings(agencyId) {
    return this.makeRequest(`/api/agency-admin/settings?agencyId=${agencyId}`);
  }

  async updateAgencySettings(agencyId, settings) {
    return this.makeRequest(`/api/agency-admin/settings`, {
      method: 'PUT',
      body: JSON.stringify({ agencyId, ...settings })
    });
  }

  async getSubscriptionInfo(agencyId) {
    return this.makeRequest(`/api/agency-admin/subscription?agencyId=${agencyId}`);
  }

  async updateSubscription(agencyId, planId) {
    return this.makeRequest(`/api/agency-admin/subscription`, {
      method: 'PUT',
      body: JSON.stringify({ agencyId, planId })
    });
  }

  // Utility Methods
  async exportData(agencyId, dataType, format = 'csv') {
    return this.makeRequest(`/api/agency-admin/export/${dataType}?format=${format}&agencyId=${agencyId}`);
  }

  async getAgencyStats(agencyId) {
    return this.makeRequest(`/api/agency-admin/stats?agencyId=${agencyId}`);
  }

  // Activity Log
  async getActivityLog(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.makeRequest(`/api/agency-admin/activity-log?${queryParams}`);
  }
}

// Create and export a singleton instance
const agencyAdminService = new AgencyAdminService();
export default agencyAdminService;
